import { analytics } from '~entrypoints/analytics';
import SpreedlyPaymentService from '../../spreedly-payment-service';
import capitalize from '~Utils/capitalize';
import isEmailAddress from '~Utils/is-email-address';

const BLANK_FIELD_ERROR = 'errors.blank';
const INVALID_FIELD_ERROR = 'errors.invalid';
const BLANK_CARD_TYPE_ERROR = 'errors.blank_card_type';
const EXPIRED_CARD_ERROR = 'errors.expired';
const CONFIGURATION_ERROR = 'errors.configuration';
const AUTHENTICATION_ERROR = 'errors.invalid_enhanced_authentication_params';

export default function ({
  envKey = undefined,
  defaultCardHolderEmail = '',
  defaultSubmitButtonText,
  leadSource,
  program,
  cardNumberPlaceholder,
  requireCountry = false,
  requireCardholderEmail = true,
}) {
  return {
    // FORM FIELDS
    cardHolderEmail: defaultCardHolderEmail || '',
    cardHolderFullName: '',
    userEmail: '',
    month: '',
    year: '',
    isGift: false,
    token: '',
    sourceSystem: '',
    country: '',
    postalCode: '',
    address1: '',
    address2: '',
    state: '',
    city: '',

    // FORM STATE
    errors: [],
    cardType: '',
    isEnabled: false,
    isReady: false,
    browserInfo: '',
    isCvvValid: undefined,
    isNumberValid: undefined,
    isExpiryValid: undefined,
    isCardFieldFocused: false,
    isErrorMessagesDisplayed: false,

    init() {
      this.leadSource = leadSource;
      this.program = program;
      this.submitButtonLabel = defaultSubmitButtonText;
      this.service = new SpreedlyPaymentService(envKey);
    },

    async install() {
      this.service.onScriptError = (msg, source, lineno, colno, err) => {
        console.log([msg, source, lineno, colno, err]);
        this.$dispatch(
          'flash-message',
          'An error has occurred. Please try again or contact support.'
        );
      };

      await this.service.install();

      this.service.on('ready', (Spreedly) => {
        this.isReady = true;
        this.isEnabled = true;

        Spreedly.toggleAutoComplete();

        Spreedly.setFieldType('number', 'text');
        Spreedly.setNumberFormat('prettyFormat');
        Spreedly.setPlaceholder('number', cardNumberPlaceholder);

        Spreedly.setStyle(
          'number',
          'outline:none;width:100%;height:2.5rem;padding:0 0.8rem 0 2.4rem;font-size:1rem;font-family:tk-proxima-nova, proxima-nova, Arial, Helvetica, sans-serif;'
        );

        Spreedly.setPlaceholder('cvv', 'CVC');
        Spreedly.setStyle(
          'cvv',
          'outline:none;height:2.5rem;font-size:1rem;padding:0 0.8rem;font-size:1rem;font-family:tk-proxima-nova, proxima-nova, Arial, Helvetica, sans-serif;'
        );
      });

      this.service.on('fieldEvent', (_Spreedly, name, type, _activeElement, inputProperties) => {
        if (type === 'input') {
          const { cardType } = inputProperties;
          this.cardType = cardType;
        }

        if (type === 'focus') {
          this.isCardFieldFocused = true;
        }

        if (type === 'mouseout') {
          this.isCardFieldFocused = false;
        }

        if (name === 'number') {
          const { cardType, numberLength } = inputProperties;

          /**
           * User has either entered max number of digits for a card, or we need
           * to correct the tabbing behaviour for the form.
           */
          if (this.isMaxCardLength(cardType, numberLength)) {
            this.$refs['cc-month'].focus();
          }

          if (type === 'shiftTab') {
            this.isCardFieldFocused = false;
          }
        }

        if (name === 'cvv') {
          if (type === 'tab') {
            this.isCardFieldFocused = false;
          }

          if (type === 'shiftTab') {
            this.$refs['cc-year'].focus();
          }

          if (type === 'enter') {
            this.triggerValidation();
          }
        }
      });

      this.service.on('validation', (_Spreedly, inputProperties) => {
        this.updateValidity(inputProperties);
        this.isErrorMessagesDisplayed = true;

        if (this.checkValidity(inputProperties)) {
          const { cardHolderOptions } = this;
          this.service.tokenizeCreditCard(cardHolderOptions);
        } else {
          this.isEnabled = true;

          const { validCvv, validNumber } = inputProperties;
          const errors = [];

          if (!validNumber) {
            errors.push({
              key: INVALID_FIELD_ERROR,
              attribute: 'number',
              message: 'Card number is invalid',
            });
          }

          if (!this.isMonthValid) {
            errors.push({
              key: INVALID_FIELD_ERROR,
              attribute: 'month',
              message: 'Month is invalid',
            });
          }

          if (!this.isYearValid) {
            errors.push({
              key: INVALID_FIELD_ERROR,
              attribute: 'year',
              message: 'Year is invalid',
            });
          }

          // Only show one error at a time
          if (!validCvv) {
            errors.push({
              key: INVALID_FIELD_ERROR,
              attribute: 'cvv',
              message: 'CVV is invalid',
            });
          }

          this.processErrors(errors);
        }
      });

      this.service.on('paymentMethod', async (Spreedly, token /*, _paymentMethod */) => {
        let {
          cardHolderOptions: { email },
        } = this;

        const {
          $store: { sessionId: distinctId },
          leadSource,
          program,
        } = this;

        try {
          analytics.identify(distinctId, { email, program, leadSource });
        } catch {
          /* jslint:disable:no-empty */
        }

        this.token = token;
        this.sourceSystem = 'Spreedly';

        let browserInfoField = document.getElementById('browser_info');
        if (browserInfoField) {
          // Choose a browser size for your application. This will be the size of the challenge
          // iframe that will be presented to a user. *note* If you're creating a modal, you
          // should make the surrounding DOM node a little larger than the option selected
          // below.
          //
          // '01' - 250px x 400px
          // '02' - 390px x 300px
          // '03' - 500px x 600px
          // '04' - 600px x 400px
          // '05' - fullscreen
          let browserSize = '04';

          // The accept header from your server side rendered page.
          let acceptHeader = document.head.querySelector('meta[name=accept_header]').content;

          // The request should include the browser data collected by using `Spreedly.ThreeDS.serialize().
          let browserInfo = Spreedly.ThreeDS.serialize(browserSize, acceptHeader);
          this.browserInfo = browserInfo;
        }

        // NOTE: You need to wait for the update above to render before submitting
        await this.$nextTick();

        this.$refs['payment-form'].submit();
      });

      this.service.on('errors', (_Spreedly, errors) => {
        this.isEnabled = true;
        this.processErrors(errors);
      });

      this.service.installForm({
        cvvElId: this.$refs['cc-cvv'].id,
        numberElId: this.$refs['cc-number'].id,
      });
    },

    requireState() {
      return ['IN' /*'CA', 'US'*/].includes(this.country);
    },

    requirePostalCode() {
      return ['IN'].includes(this.country);
    },

    requireCity() {
      return ['IN'].includes(this.country);
    },

    requireAddress1() {
      return ['IN'].includes(this.country);
    },

    updateValidity(inputProperties) {
      const { validCvv, validNumber } = inputProperties;
      this.isCvvValid = validCvv;
      this.isNumberValid = validNumber;
    },

    checkValidity(inputProperties) {
      const {
        isCardHolderEmailValid,
        isCardHolderFullNameValid,
        isPostalCodeValid,
        isAddress1Valid,
        isAddress2Valid,
        isStateValid,
        isCityValid,
        isCountryValid,
        isGiftValid,
        isMonthValid,
        isYearValid,
      } = this;
      const { validCvv, validNumber } = inputProperties;

      return (
        validNumber &&
        validCvv &&
        isCardHolderFullNameValid &&
        isPostalCodeValid &&
        isAddress1Valid &&
        isAddress2Valid &&
        isStateValid &&
        isCityValid &&
        isCountryValid &&
        isCardHolderEmailValid &&
        isGiftValid &&
        isMonthValid &&
        isYearValid
      );
    },

    isMaxCardLength(cardType, numberLength) {
      switch (cardType) {
        case 'american_express':
          return numberLength === 15;
        case 'visa':
        case 'master':
          return numberLength === 16;
        default:
          return false;
      }
    },

    get cardHolderOptions() {
      return {
        email: this.cardHolderEmail,
        fullName: this.cardHolderFullName,

        address1: this.address1 || null,
        address2: this.address2 || null,
        city: this.city || null,
        state: this.state || null,
        zip: this.postalCode || null,
        country: this.country || null,

        month: this.month,
        year: this.year,
      };
    },

    get isCardHolderEmailValid() {
      return !requireCardholderEmail || isEmailAddress(this.cardHolderEmail);
    },

    get isCardHolderEmailInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCardHolderEmailValid;
    },

    get isCardHolderFullNameValid() {
      return this.cardHolderFullName.length > 1;
    },

    get isCardHolderFullNameInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCardHolderFullNameValid;
    },

    get isPostalCodeValid() {
      return !this.requirePostalCode() || this.postalCode.length > 1;
    },

    get isPostalCodeInvalid() {
      return this.isErrorMessagesDisplayed && !this.isPostalCodeValid;
    },

    get isAddress1Valid() {
      return !this.requireAddress1() || this.address1.length > 1;
    },

    get isAddress1Invalid() {
      return this.isErrorMessagesDisplayed && !this.isAddress1Valid;
    },

    get isAddress2Valid() {
      return true;
    },

    get isAddress2Invalid() {
      return !this.isAddress2Valid;
    },

    get isStateValid() {
      return !this.requireState() || this.state.length > 1;
    },

    get isStateInvalid() {
      return this.isErrorMessagesDisplayed && !this.isStateValid;
    },

    get isCityValid() {
      return !this.requireCity() || this.city.length > 1;
    },

    get isCityInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCityValid;
    },

    get isCountryValid() {
      return !requireCountry || this.country.length == 2;
    },

    get isCountryInvalid() {
      return this.isErrorMessagesDisplayed && !this.isCountryValid;
    },

    get isGiftValid() {
      if (!this.isGift) {
        return true;
      }

      return isEmailAddress(this.userEmail);
    },

    get isGiftInvalid() {
      return this.isErrorMessagesDisplayed && !this.isGiftValid;
    },

    get isMonthValid() {
      const month = parseInt(this.month);
      if (isNaN(month)) {
        return false;
      }

      if (month < 1 || month > 12) {
        return false;
      }

      if (!this.isYearValid) {
        return true;
      }

      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // getMonth() is zero-based

      return this.year > currentYear || (this.year == currentYear && month >= currentMonth);
    },

    get isYearValid() {
      return parseInt(this.year) >= new Date().getFullYear();
    },

    get isCardValid() {
      const { isCvvValid, isNumberValid, isExpiryValid } = this;

      return [isCvvValid, isNumberValid, isExpiryValid].every((field) => {
        return typeof field === 'undefined' || field;
      });
    },

    processErrors(errors) {
      const errorsNotHandledAutomatically = [];
      const cardErrors = [];

      try {
        analytics.track('Checkout Validation Failure');
      } catch {
        /* jslint:disable:no-empty */
      }

      // Handle validation errors
      errors.forEach(({ key, attribute, message }) => {
        const error = {
          key,
          attribute,
          message,
        };

        if (['number', 'cvv', 'month', 'year'].includes(attribute)) {
          switch (key) {
            // Known error keys for invalid credit card details
            case BLANK_FIELD_ERROR:
            case INVALID_FIELD_ERROR:
            case BLANK_CARD_TYPE_ERROR:
              cardErrors.push(error);

              if (['month', 'year'].includes(attribute)) {
                this.isExpiryValid = false;
              } else {
                this[`is${capitalize(attribute)}Valid`] = false;
              }

              break;

            case EXPIRED_CARD_ERROR:
              cardErrors.push(error);

              this.isExpiryValid = false;
              break;

            default:
              throw 'unrecognized error!';
          }
        } else {
          if (key === CONFIGURATION_ERROR) {
            throw 'Recache not yet handled. Please submit PR.';
          } else if (key === AUTHENTICATION_ERROR) {
            console.log([this.cardHolderEmail, error]);
          } else {
            errorsNotHandledAutomatically.push(error);
          }
        }
      });

      this.errors = cardErrors;
      this.submitButtonLabel = 'Try again';
    },

    resetValidations() {
      this.isCvvValid = undefined;
      this.isNumberValid = undefined;
      this.isExpiryValid = undefined;
      this.errors = [];
    },

    triggerValidation() {
      const { cardHolderOptions } = this;

      try {
        analytics.track('Checkout Button Pressed');
      } catch {
        /* jslint:disable:no-empty */
      }

      this.isEnabled = false;
      this.resetValidations();
      this.service.validate(cardHolderOptions);
    },
  };
}
